import React from 'react';
import { Box, Stack, Typography, SxProps, Theme, useTheme } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { EliceBadge } from '../badge';
import { useAIDTMediaQuery } from '../../hooks';

export interface InnerHeaderProps {
  title: string;
  showQuestionIcon?: boolean;
  stepNumber?: number;
  badgeLabel?: string;
  sx?: SxProps<Theme>;
}

const InnerHeader = ({
  title,
  showQuestionIcon = false,
  stepNumber,
  badgeLabel,
  sx,
}: InnerHeaderProps): JSX.Element => {
  const theme = useTheme();
  const { isTablet } = useAIDTMediaQuery();

  // 우선순위: badgeLabel > showQuestionIcon > stepNumber
  const containerBaseSx: SxProps<Theme> = {
    width: '100%',
    mx: 'auto',
    minHeight: isTablet ? '3.2rem' : '4rem',
    backgroundColor: 'transparent',
    paddingLeft: isTablet ? '1rem' : '1.875rem',
    paddingRight: isTablet ? '1rem' : '1.875rem',
    py: isTablet ? '0.5rem' : '1rem',
    margin: 0,
  };

  const mergedContainerSx: SxProps<Theme> =
    sx !== undefined
      ? [containerBaseSx, ...(Array.isArray(sx) ? sx : [sx])]
      : containerBaseSx;

  const gap = isTablet ? '0.75rem' : '1rem';
  const iconSize = isTablet ? '1.25rem' : '1.5rem';

  // Mode 1: Badge Label Mode
  if (badgeLabel) {
    return (
      <Box
        display="flex"
        alignItems="flex-start"
        gap={gap}
        sx={mergedContainerSx}
      >
        <Stack
          sx={{
            display: 'flex',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <EliceBadge
            label={badgeLabel}
            color="primary"
          />
        </Stack>
        <Typography
          variant="h2"
          color="text.primary"
          sx={{
            fontWeight: 600,
            lineHeight: 1.4,
            mt: { xs: 0.3, md: 0.2 },
            flex: 1,
            wordBreak: 'keep-all',
            whiteSpace: 'normal',
          }}
        >
          {title}
        </Typography>
      </Box>
    );
  }

  // Mode 2: Question Icon Mode
  if (showQuestionIcon) {
    return (
      <Box
        display="flex"
        gap={gap}
        sx={mergedContainerSx}
      >
        <Stack
          sx={{
            display: 'flex',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            mt: isTablet ? '0.2rem' : '0.6rem'
          }}
        >
          <Box
            sx={{
              width: iconSize,
              height: iconSize,
              display: 'flex',
              flexShrink: 0,
            }}
          >
            <HelpOutlineIcon
              sx={{
                fontSize: iconSize,
                color: '#7c3aed',
              }}
            />
          </Box>
        </Stack>
        <Typography
          variant="h2"
          color="text.primary"
          sx={{
            fontWeight: 600,
            lineHeight: 1.4,
            mt: { xs: 0.3, md: 0.2 },
            flex: 1,
            wordBreak: 'keep-all',
            whiteSpace: 'normal',
          }}
        >
          {title}
        </Typography>
      </Box>
    );
  }

  // Mode 3: Step Number Mode or Title Only
  const stepGap = isTablet ? '0.75rem' : '1rem';
  return (
    <Box
      display="flex"
      gap={stepGap}
      sx={mergedContainerSx}
    >
      {stepNumber && (
        <Box
          sx={{
            width: iconSize,
            height: iconSize,
            borderRadius: '50%',
            backgroundColor: '#7c3aed',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: isTablet ? '0.8125rem' : '0.875rem',
            fontWeight: 'bold',
            flexShrink: 0,
            mt: isTablet ? '0.2rem' : '0.6rem'
          }}
        >
          {stepNumber}
        </Box>
      )}
      <Typography
        variant="h2"
        color="text.primary"
        sx={{
          fontWeight: 600,
          lineHeight: 1.4,
          mt: { xs: 0.3, md: 0.2 },
          flex: 1,
          wordBreak: 'keep-all',
          whiteSpace: 'normal',
          fontSize: isTablet
            ? theme.typography.h2.fontSize
            : theme.typography.h2?.fontSize,
        }}
      >
        {title}
      </Typography>
    </Box>
  );
};

export default InnerHeader;
